import React, { memo } from "react";
import Table from "react-bootstrap/Table";
import OverlayTrigger from "react-bootstrap/OverlayTrigger";
import Tooltip from "react-bootstrap/Tooltip";
import StatusChip from "./StatusChip";
import "./styles.scss";
import OptionsMenu from "./OptionsMenu";
import DragAndDropContainer from "../DragAndDrop/index";
import FileName from "./FileName";
import moment from "moment";
import LazyLoad from "react-lazyload";
import {
  isDeleteOwnAccess,
  isEditOwnAccess,
} from "../../utils/permissionsParser";
import RenderLatexContent from "../RenderLatexContent";
import { useNavigate } from "react-router-dom";

const getResponsiveFont = (desktop, mobile) =>
  window.innerWidth <= 1024 ? `${mobile} !important` : desktop;

const dataStyles = {
  textDecoration: "none",
  color: "#5A6A85",
  fontFamily: "Plus Jakarta Sans",
  fontSize: getResponsiveFont("14px", "12px"),
  fontWeight: "500",
  textAlign: "left",
  lineHeight: getResponsiveFont("normal", "16px"),
};

const headerStyles = {
  textDecoration: "none",
  color: "#534D59",
  fontFamily: "Plus Jakarta Sans",
  fontSize: getResponsiveFont("16px", "14px"),
  fontWeight: "600",
  textAlign: "left",
  lineHeight: getResponsiveFont("normal", "18px"),
};

const titleStyles = {
  textDecoration: "none",
  color: "#1B2128",
  fontFamily: "Plus Jakarta Sans",
  fontSize: getResponsiveFont("14px", "12px"),
  fontWeight: "600",
  textAlign: "left",
  width: "auto",
  lineHeight: getResponsiveFont("normal", "16px"),
};

const statusStyles = {
  textDecoration: "none",
  fontSize: "12px",
  fontWeight: "400",
  textAlign: "left",
  lineHeight: "16px",
  textUnderlinePosition: "from-font",
  textDecorationSkipInk: "none",
};

export const TypeBadge = ({ data, style, onClick }) => {
  return (
    <div onClick={onClick}>
      <div
        style={{
          backgroundColor: "#F1F3FA",
          color: "#8B97A8",
          padding: "4px 8px",
          margin: "0px 2px",
          textTransform: "capitalize",
          borderRadius: "75.11px",
          fontSize: "14px",
          ...style,
        }}
      >
        {data}
      </div>
    </div>
  );
};

function truncateText(text, maxLength) {
  if (text?.length > maxLength) {
    return text?.substring(0, maxLength) + "...";
  }
  return text;
}

export const getValidOptionsList = (
  optionsList,
  permissions,
  userId,
  createdBy,
  isPermissionApplied
) => {
  if (!isPermissionApplied) {
    return optionsList;
  }
  let options = [...optionsList];

  if (!permissions?.edit && !isEditOwnAccess(permissions, userId, createdBy)) {
    options = options.filter(
      (option) =>
        option?.text !== "Edit" &&
        option?.text !== "View" &&
        typeof option?.text !== "function" &&
        option?.text !== "Deactivate" &&
        option?.text !== "Enable Channel" &&
        option?.text !== "Closed Channel"
    );
  }
  if (
    !permissions?.delete &&
    !isDeleteOwnAccess(permissions, userId, createdBy)
  ) {
    options = options.filter((option) => option?.text !== "Delete");
  }
  return options;
};

const DataTable = ({
  tableType,
  data,
  headers,
  optionsMenuList,
  showHeader,
  dragDrop = null,
  handleDragEnd = () => {},
  deleteFile = () => {},
  EditFile = () => {},
  deleteButton = null,
  renderLastRow = () => {},
  renderConcepts = null,
  border = null,
  renderEditHeading = () => {},
  showCount = false,
  page = -1,
  pageSize = 10,
  isResponsive = false,
  isPermissionApplied = false,
  permissions = {},
  permissionDialogHandler = () => {},
  role = "Student",
  userId = "",
  containerId = "",
  defaulltText = "",
  onClickOnName = () => {},
  nameStyles = {},
  showNumbering = true,
  isOnCompleteRow = false,
}) => {
  const navigate = useNavigate();
  const getColumnData = (header, row, index) => {
    if (header === "heading") {
      return renderEditHeading(row, index);
    }

    // Existing column rendering logic
    if (header === "progress") {
      return (
        <div>
          <div
            className="view-progess-label"
            onClick={() =>
              navigate(`/users-management/student-report/${row._id}`)
            }
          >
            View
          </div>
        </div>
      );
    }

    if (header === "name" || header === "studentsEnrolled") {
      return (
        <div
          onClick={onClickOnName.bind(null, row)}
          style={{ ...titleStyles, ...nameStyles }}
        >
          {row[header]}
        </div>
      );
    }
    if (header === "type") {
      return (
        <TypeBadge
          onClick={() => {
            if (isOnCompleteRow) {
              onClickOnName(row);
            }
          }}
          key={index}
          data={row[header]}
        />
      );
    }
    if (header === "subscriptions" || header === "classrooms") {
      return (
        <div style={{ width: "100%", display: "flex", alignItems: "center" }}>
          {row[header] && row[header].length > 0 ? (
            <OverlayTrigger
              overlay={(props) => (
                <Tooltip
                  id={`tooltip-${index}`}
                  {...props}
                  style={{ maxWidth: "300px" }}
                >
                  {row[header]?.map((data, index) => (
                    <div
                      key={index}
                      style={{
                        fontSize: "14px",
                      }}
                    >
                      {typeof data === "object" &&
                      data !== null &&
                      "classroomName" in data
                        ? data.classroomName
                        : JSON.stringify(data)}
                    </div>
                  ))}
                </Tooltip>
              )}
              placement="bottom"
            >
              <div
                key={index}
                style={{
                  backgroundColor: "#F1F3FA",
                  color: "#8B97A8",
                  padding: "0px 2px",
                  margin: "0px 2px",
                  textTransform: "capitalize",
                  border: "1px solid #8B97A8",
                  borderRadius: "3px",
                  fontSize: "14px",
                  display: "inline-block",
                }}
              >
                {`${
                  row[header] && row[header][0]
                    ? row[header][0].classroomName ||
                      JSON.stringify(row[header][0])
                    : "Trial"
                }${row[header]?.length > 1 ? "..." : ""}`}
              </div>
            </OverlayTrigger>
          ) : role === "Teacher" ? (
            "-"
          ) : (
            `${defaulltText ? defaulltText : "Trial"}`
          )}
        </div>
      );
    }

    if (header === "difficultyLevel") {
      return <StatusChip status={row[header]} />;
    }
    if (header === "status") {
      return (
        <div
          style={{
            ...statusStyles,
            color:
              row[header] === "Inactive"
                ? "red"
                : row[header] === "Active"
                ? "green"
                : "black",
          }}
        >
          {row[header]}
        </div>
      );
    }
    if (header === "fileName") {
      return (
        <FileName
          row={row}
          header={header}
          index={index}
          deleteFile={deleteFile}
          EditFile={EditFile}
          disabled={
            isPermissionApplied
              ? row[header] !== ""
                ? !permissions?.edit &&
                  !isEditOwnAccess(permissions, userId, row.createdBy)
                : false
              : false
          }
        />
      );
    }
    if (header === "startDate" || header === "endDate") {
      return (
        <div style={dataStyles}>
          {row[header] ? moment(row[header]).format(`DD/MM/YYYY`) : ""}
        </div>
      );
    }
    if (header === "concepts") {
      if (renderConcepts !== null) {
        return renderConcepts(row[header], index, row);
      } else {
        return (
          <div style={{ titleStyles }}>
            {Array.isArray(row[header]) ? row[header].join(", ") : row[header]}
          </div>
        );
      }
    }
    if (header === "linkedWith") {
      if (renderConcepts !== null) {
        return renderConcepts(row[header], index, row);
      } else {
        return (
          <div style={titleStyles}>
            {Array.isArray(row[header])
              ? row[header].map((set) => {
                  return <StatusChip status={set?.name} />;
                })
              : row[header]}
          </div>
        );
      }
    }
    if (header === "size") {
      return <div>{Math.round(row[header] / 1000)}KB</div>;
    }
    return (
      <div
        onClick={() => {
          if (isOnCompleteRow) {
            onClickOnName(row);
          }
        }}
        style={dataStyles}
      >
        {Array.isArray(row[header]) ? (
          row[header].map((item, index) => (
            <React.Fragment key={index}>
              {item}
              {index !== row[header].length - 1 && ", "}
            </React.Fragment>
          ))
        ) : (
          <>
            {typeof row[header] === "string" ? (
              <span
                style={{
                  width: `${tableType === "questionBank" ? "300px" : "auto"}`,
                }}
              >
                {/* <FormattedText text={row[header]} maxLength={130} /> */}
                <RenderLatexContent content={row[header]} />
              </span>
            ) : (
              <span
                style={{
                  width: `${tableType === "questionBank" ? "300px" : "auto"}`,
                }}
              >
                {truncateText(row[header], 200)}
              </span>
            )}
          </>
        )}
      </div>
    );
  };

  const renderItem = (item, index) => {
    return (
      <>
        {showCount && (
          <td>
            <div>{(page - 1) * pageSize + index + 1}</div>
          </td>
        )}
        {headers.map((header) => (
          <td key={`${item._id}-${header.id}`}>
            {getColumnData(header.id, item, index)}
          </td>
        ))}
        {optionsMenuList &&
        getValidOptionsList(
          optionsMenuList,
          permissions,
          userId,
          item?.createdBy,
          isPermissionApplied
        ).length !== 0 ? (
          <td>
            {optionsMenuList.length > 0 && (
              <OptionsMenu
                optionsList={getValidOptionsList(
                  optionsMenuList,
                  permissions,
                  userId,
                  item?.createdBy,
                  isPermissionApplied
                )}
                passedData={item._id}
                row={item}
              />
            )}
          </td>
        ) : (
          <td />
        )}
        {deleteButton && (
          <td>
            {(!isPermissionApplied ||
              (isPermissionApplied &&
                (permissions?.delete ||
                  isDeleteOwnAccess(
                    permissions,
                    userId,
                    item?.createdBy
                  )))) && (
              <div>
                <img
                  src={deleteButton?.imgSrc}
                  alt={deleteButton?.imgAltText}
                  onClick={() =>
                    deleteButton?.actionsHandler(item._id, containerId)
                  }
                  style={{ cursor: "pointer" }}
                />
              </div>
            )}
          </td>
        )}
      </>
    );
  };

  return (
    <Table
      className="classroom-table mb-0 pl-2"
      hover
      responsive={isResponsive}
      bordered={border === true ? true : false}
    >
      {showHeader && (
        <thead>
          <tr>
            {showNumbering && (
              <th
                style={{
                  justifyContent: "center",
                  alignItems: "center",
                  width: "auto",
                }}
              >
                <div style={{ paddingBottom: "14px" }}>#</div>
              </th>
            )}
            {headers.map((header) => (
              <th
                style={{
                  ...headerStyles,
                  width: header?.width ? header?.width : "",
                }}
                key={header.id}
              >
                <div
                  style={{
                    paddingBottom: "14px",
                    ...(header?.styles ? header.styles : {}),
                  }}
                >
                  {header.title}
                </div>
              </th>
            ))}
            {(optionsMenuList || deleteButton) && (
              <th
                style={{
                  justifyContent: "center",
                  alignItems: "center",
                  width: "auto",
                }}
              />
            )}
          </tr>
        </thead>
      )}
      {dragDrop ? (
        <DragAndDropContainer
          items={data.map((item, index) => ({ ...item, index }))}
          onDragEnd={handleDragEnd}
          renderItem={renderItem}
          renderLastRow={renderLastRow}
          permissionDialogHandler={permissionDialogHandler}
          permissions={permissions}
          userId={userId}
        />
      ) : (
        <tbody>
          {data.map((item, index) => (
            <tr key={item._id}>
              {showNumbering && (
                <td>
                  <div
                    onClick={() => {
                      if (isOnCompleteRow) {
                        onClickOnName(item);
                      }
                    }}
                  >
                    {page > 0 ? (page - 1) * pageSize + index + 1 : index + 1}
                  </div>
                </td>
              )}
              {headers.map((header) => (
                <td key={`${item._id}-${header.id}`}>
                  {header?.styles ? (
                    <div style={header?.styles}>
                      {getColumnData(header.id, item, index)}
                    </div>
                  ) : (
                    getColumnData(header.id, item, index)
                  )}
                </td>
              ))}
              {optionsMenuList &&
              getValidOptionsList(
                optionsMenuList,
                permissions,
                userId,
                item?.createdBy,
                isPermissionApplied
              ).length !== 0 ? (
                <td>
                  <OptionsMenu
                    optionsList={getValidOptionsList(
                      optionsMenuList,
                      permissions,
                      userId,
                      item?.createdBy,
                      isPermissionApplied
                    )}
                    passedData={item._id}
                    row={item}
                    dropdownStyles={{
                      display: "flex",
                      justifyContent: "end",
                    }}
                  />
                </td>
              ) : (
                <td />
              )}
              {deleteButton && (
                <td>
                  <div>
                    <LazyLoad height={20} offset={100}>
                      <img
                        src={deleteButton.imgSrc}
                        alt={deleteButton.imgAltText}
                        onClick={() =>
                          deleteButton.actionsHandler(item._id, containerId)
                        }
                        style={{ cursor: "pointer" }}
                      />
                    </LazyLoad>
                  </div>
                </td>
              )}
            </tr>
          ))}
        </tbody>
      )}
    </Table>
  );
};

export default memo(DataTable);
